import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Button, Form, Table, Alert, Card, Badge } from 'react-bootstrap';
import addIcon from '../assets/icons/add.svg';
import closeIcon from '../assets/icons/close.svg';
import { AddFinancialEntryModal } from './AddFinancialEntryModal';
import US_STATES from '../utils/usStates';
import { usePaginatedData } from '../hooks/usePaginatedData';
import { useFormData } from '../hooks/useFormData';
import { useStores } from '../hooks/useStores';
import { useURLParams } from '../hooks/useURLParams';
import { useColumnConfig } from '../hooks/useColumnConfig';
import { StatusBadge } from './shared/StatusBadge';
import { ColumnConfigDropdown } from './shared/ColumnConfigDropdown';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
  { key: 'year', label: 'Year' },
  { key: 'color', label: 'Color' },
  { key: 'license_plate', label: 'License Plate' },
  { key: 'state', label: 'State' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions' },
];

/**
 * VehicleList component displays all vehicles in a table
 * and provides navigation to detail view and creation
 */
export function VehicleList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCall, user } = useOutletContext();
  const [showNewVehicleForm, setShowNewVehicleForm] = useState(false);
  const [showFinancialEntryModal, setShowFinancialEntryModal] = useState(false);

  // URL-synced filters and pagination
  const { getParam, setParam, setParams } = useURLParams({ store_id: '', page: 0 });
  const selectedStore = getParam('store_id');
  const urlPage = getParam('page');

  // Column config from URL
  const { visibleColumns, isVisible, toggleColumn, allColumns } = useColumnConfig(COLUMNS);

  // FIXED: Use user context instead of manually parsing JWT
  const userRole = user?.role || 'agent';

  // Use stores hook
  const { stores } = useStores(apiCall);

  // Use paginated data hook with store filter
  const {
    data: filteredVehicles,
    loading,
    error: loadError,
    pagination,
    reload,
    goToNextPage: _goToNextPage,
    goToPreviousPage: _goToPreviousPage,
  } = usePaginatedData(
    async (queryParams) => apiCall(`${API_BASE_URL}/vehicles?${queryParams}`),
    { limit: 20, filters: { store_id: selectedStore }, dependencies: [apiCall], initialOffset: urlPage * 20 }
  );

  // Wrap pagination to sync URL
  const goToNextPage = useCallback(() => {
    _goToNextPage();
    const nextPage = Math.floor(pagination.offset / pagination.limit) + 1;
    setParam('page', nextPage, { replace: true });
  }, [_goToNextPage, pagination, setParam]);

  const goToPreviousPage = useCallback(() => {
    _goToPreviousPage();
    const prevPage = Math.floor(pagination.offset / pagination.limit) - 1;
    setParam('page', prevPage > 0 ? prevPage : '', { replace: true });
  }, [_goToPreviousPage, pagination, setParam]);

  // Auto-open form if navigated from Quick Actions
  useEffect(() => {
    if (location.state?.openAddForm) {
      setShowNewVehicleForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Use form data hook for vehicle form
  const {
    formData,
    setFormData,
    handleChange: handleFormChange,
    resetForm,
  } = useFormData({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    vin: '',
    license_plate: '',
    license_plate_state: 'IN',
    class: 'full_size_sedan',
    store_id: 1,
  }, {
    typeMap: { year: 'integer', store_id: 'integer' }
  });

  const [formError, setFormError] = useState(null);

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setFormError(null);

    try {
      const response = await apiCall(
        `${API_BASE_URL}/vehicles`,
        {
          method: 'POST',
          body: JSON.stringify(formData),
        }
      );

      if (response.success || response) {
        resetForm();
        setShowNewVehicleForm(false);
        alert('Vehicle added successfully');
        reload();
      } else {
        setFormError(response.error || 'Failed to add vehicle');
      }
    } catch (error) {
      setFormError(error.message || 'Error adding vehicle');
    }
  };

  const renderCell = (key, vehicle) => {
    switch (key) {
      case 'id': return vehicle.id;
      case 'make': return vehicle.make;
      case 'model': return vehicle.model;
      case 'year': return vehicle.year;
      case 'color': return vehicle.color || '-';
      case 'license_plate': return vehicle.license_plate;
      case 'state': return vehicle.license_plate_state || '-';
      case 'status': return <StatusBadge status={vehicle.status} type="vehicle" />;
      case 'actions':
        return (
          <Button variant="link" size="sm" onClick={() => navigate(`./${vehicle.id}`)}>
            View Details
          </Button>
        );
      default: return null;
    }
  };

  return (
    <Container fluid className="py-4 domain-vehicles">
      <Row className="mb-4">
        <Col>
          <h2>Vehicles</h2>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          {user?.role !== 'auditor' && (
            <>
              <Button
                variant={showNewVehicleForm ? "secondary" : "primary"}
                onClick={() => setShowNewVehicleForm(!showNewVehicleForm)}
                className="me-2"
              >
                {showNewVehicleForm ? <><img src={closeIcon} alt="" className="icon-btn" />Cancel</> : <><img src={addIcon} alt="" className="icon-btn" />Add New Vehicle</>}
              </Button>
              {(userRole === 'manager' || userRole === 'admin') && (
                <Button
                  variant="primary"
                  onClick={() => setShowFinancialEntryModal(true)}
                >
                  <img src={addIcon} alt="" className="icon-btn" />
                  New Repair / Maintenance
                </Button>
              )}
            </>
          )}
        </Col>
        <Col md={8} className="d-flex align-items-center justify-content-end gap-3">
          {stores.length > 1 && (
            <>
              <Form.Label className="mb-0 fw-bold">Filter by Store:</Form.Label>
              <Form.Select
                value={selectedStore}
                onChange={(e) => setParams({ store_id: e.target.value, page: '' })}
                style={{ maxWidth: '250px' }}
              >
                <option value="">All Stores</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </Form.Select>
              {selectedStore && (
                <Badge bg="secondary">
                  {filteredVehicles.length} vehicles
                </Badge>
              )}
            </>
          )}
          <ColumnConfigDropdown allColumns={allColumns} isVisible={isVisible} toggleColumn={toggleColumn} />
        </Col>
      </Row>

      {showNewVehicleForm && (
        <Card className="mb-4">
          <Card.Header className="text-white" style={{ backgroundColor: '#0d9488' }}>
            <h3>Add New Vehicle</h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleAddVehicle}>
              {formError && <Alert variant="danger">{formError}</Alert>}

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Make</Form.Label>
                    <Form.Control
                      type="text"
                      name="make"
                      placeholder="Make"
                      value={formData.make}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Model</Form.Label>
                    <Form.Control
                      type="text"
                      name="model"
                      placeholder="Model"
                      value={formData.model}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Year</Form.Label>
                    <Form.Control
                      type="number"
                      name="year"
                      placeholder="Year"
                      value={formData.year}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Color</Form.Label>
                    <Form.Control
                      type="text"
                      name="color"
                      placeholder="Color"
                      value={formData.color}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>VIN</Form.Label>
                    <Form.Control
                      type="text"
                      name="vin"
                      placeholder="VIN"
                      value={formData.vin}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>License Plate</Form.Label>
                    <Form.Control
                      type="text"
                      name="license_plate"
                      placeholder="License Plate"
                      value={formData.license_plate}
                      onChange={handleFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>License Plate State *</Form.Label>
                    <Form.Select
                      name="license_plate_state"
                      value={formData.license_plate_state}
                      onChange={handleFormChange}
                      required
                    >
                      {US_STATES.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.code} - {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Class</Form.Label>
                    <Form.Select
                      name="class"
                      value={formData.class}
                      onChange={handleFormChange}
                    >
                      <option value="basic_economy">Basic Economy</option>
                      <option value="full_size_sedan">Full Size Sedan</option>
                      <option value="newer_sedan_suv">Newer Sedan/SUV</option>
                      <option value="luxury">Luxury</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Store</Form.Label>
                    <Form.Select name="store_id" value={formData.store_id} onChange={handleFormChange}>
                      <option value="">Select a store</option>
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>
                          {store.name} {store.city ? `(${store.city})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Button type="submit" variant="primary">
                Save Vehicle
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body className="p-0">
          {filteredVehicles.length > 0 && (
            <div className="px-3 py-2 bg-light">
              <small className="text-muted">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} vehicles
              </small>
            </div>
          )}

          <Table striped hover responsive>
            <thead>
              <tr>
                {visibleColumns.map(col => (
                  <th key={col.key} className={col.className}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.length > 0 ? (
                filteredVehicles.map(vehicle => (
                  <tr key={vehicle.id} style={vehicle.status === 'sold' ? { opacity: 0.5, backgroundColor: '#f0f0f0' } : {}}>
                    {visibleColumns.map(col => (
                      <td key={col.key} className={col.className}>{renderCell(col.key, vehicle)}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center py-4">
                    {selectedStore ? 'No vehicles found at this store' : 'No vehicles found'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {filteredVehicles.length > 0 && pagination.total > pagination.limit && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <Button
            variant="secondary"
            onClick={goToPreviousPage}
            disabled={pagination.offset === 0}
          >
            &larr; Previous
          </Button>
          <span className="text-muted">
            Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="secondary"
            onClick={goToNextPage}
            disabled={pagination.offset + pagination.limit >= pagination.total}
          >
            Next &rarr;
          </Button>
        </div>
      )}

      <AddFinancialEntryModal
        show={showFinancialEntryModal}
        onHide={() => setShowFinancialEntryModal(false)}
        onSuccess={() => alert('Financial entry added successfully')}
        apiCall={apiCall}
        userRole={userRole}
        vehicles={filteredVehicles}
      />
    </Container>
  );
}

export default VehicleList;
